import { createAction, createReducer } from 'redux-starter-kit'
import { createSelector } from 'reselect'
import { roleuser, tutorials, subscriptions } from '@edulastic/constants'
import { isEmpty } from 'lodash'
import { getTimestampFromMongoId } from '@edulastic/common/src/helpers'
import {
  getAssignmentsByTestSelector,
  getAssignmentsSummary,
} from '../author/src/selectors/assignments'
import {
  getUserCreatedAt,
  getUserId,
  getUserRole,
  isSuperAdminSelector,
  isUserLoggedInFirstTimeOnAddAccountSelector,
} from '../author/src/selectors/user'
import { assignmentNewSearchAndFolderTutorialSteps } from '../author/Assignments/components/Container/constants'

const { PARTIAL_PREMIUM, ENTERPRISE } = subscriptions.SUBSCRIPTION_SUB_TYPES

const getSubscriptionSubType = (state) =>
  state?.subscription?.subscriptionData?.subscription?.subType

export const SET_CURRICULUM_JOYRIDE_RUN =
  '[tutorial] set curriculum joyride run'

export const SET_TEACHER_COMMON_DISTRICT_JOYRIDE_RUN =
  '[tutorial] set teacher common district joyride run'

export const SET_CURRENT_TUTORIAL = '[tutorial] set tutorial pack'

export const GET_VIEWED_TUTORIALS_REQUEST =
  '[tutorial] get viewed tutorials request'
export const GET_VIEWED_TUTORIALS_SUCCESS =
  '[tutorial] get viewed tutorials request success'
export const GET_VIEWED_TUTORIALS_FAILURE =
  '[tutorial] get viewed tutorials request failure'
export const SET_VIEWED_TUTORIALS_REQUEST = `[tutorial] set viewed tutorials request`
export const SET_VIEWED_TUTORIALS_SUCCESS = `[tutorial] set viewed tutorials success`
export const SET_VIEWED_TUTORIALS_FAILURE = `[tutorial] set viewed tutorials failure`

const initialState = {
  currentTutorial: null,
  viewedTutorials: [],
  viewedTutorialsLoaded: false,
  curriculumJoyrideRun: false,
  teacherCommonDistrictJoyrideRun: false,
  error: null,
}

export const setCurrentTutorialAction = createAction(SET_CURRENT_TUTORIAL)
export const getViewedTutorialsAction = createAction(
  GET_VIEWED_TUTORIALS_REQUEST
)
export const setViewedTutorialsAction = createAction(
  SET_VIEWED_TUTORIALS_REQUEST
)
export const setCurriculumJoyrideRunAction = createAction(
  SET_CURRICULUM_JOYRIDE_RUN
)
export const setTeacherCommonDistrictJoyrideRunAction = createAction(
  SET_TEACHER_COMMON_DISTRICT_JOYRIDE_RUN
)

const reducer = createReducer(initialState, {
  [SET_CURRENT_TUTORIAL]: (state, { payload }) => {
    state.currentTutorial = payload
  },
  [SET_CURRICULUM_JOYRIDE_RUN]: (state, { payload }) => {
    state.curriculumJoyrideRun = payload
  },
  [SET_TEACHER_COMMON_DISTRICT_JOYRIDE_RUN]: (state, { payload }) => {
    state.teacherCommonDistrictJoyrideRun = payload
  },
  [GET_VIEWED_TUTORIALS_SUCCESS]: (state, { payload }) => {
    state.viewedTutorials = payload
    state.viewedTutorialsLoaded = true
    state.error = null
  },
  [SET_VIEWED_TUTORIALS_SUCCESS]: (state, { payload }) => {
    state.viewedTutorials = payload
    state.viewedTutorialsLoaded = true
    state.error = null
  },
  [GET_VIEWED_TUTORIALS_FAILURE]: (state, { payload }) => {
    state.viewedTutorials = Object.values(tutorials)
    state.viewedTutorialsLoaded = true
    state.error = payload
  },
  [SET_VIEWED_TUTORIALS_FAILURE]: (state, { payload }) => {
    state.error = payload
  },
})

const stateSelector = (state) => state.tutorial

export const areViewedTutorialsLoadedSelector = createSelector(
  stateSelector,
  (state) => state.viewedTutorialsLoaded
)

export const viewedTutorialsSelector = createSelector(
  stateSelector,
  getUserId,
  (state, userId) => {
    if (process.env.REACT_APP_QA_ENV || !userId) {
      return Object.values(tutorials)
    }
    const userCreatedTime = getTimestampFromMongoId(userId)
    const mayThirtyFirst2024 = 1717113600000 // new Date('2024-05-31').getTime()
    if (userCreatedTime > mayThirtyFirst2024) {
      return [
        ...state.viewedTutorials,
        tutorials.newSearchAndFolderView,
      ].filter((item) => !!item)
    }
    return state.viewedTutorials
  }
)

/**
 * True when Action Hub should wait for teacher-common: DA on enterprise/
 * partial-premium and teacherCommonDistrictSetting still unseen.
 * Manage District is allowSuperAdmin-only in SideMenu — without isSuperAdmin
 * the tour target is missing, so do not block Action Hub.
 * For DA + super-admin only, also wait while subType is still loading.
 */
export const isEligibleForTeacherCommonDistrictSetting = createSelector(
  getUserRole,
  isSuperAdminSelector,
  getSubscriptionSubType,
  viewedTutorialsSelector,
  (userRole, isSuperAdmin, subType, viewedTutorials) => {
    if (userRole !== roleuser.DISTRICT_ADMIN || !isSuperAdmin) {
      return false
    }

    // DA + super-admin: subscription not ready yet — keep Action Hub deferred
    const waitingForSubType = !subType
    const isEligible =
      !waitingForSubType &&
      [PARTIAL_PREMIUM, ENTERPRISE].includes(subType) &&
      !viewedTutorials.includes(tutorials.teacherCommonDistrictSetting)

    return waitingForSubType || isEligible
  }
)

export const getAssignmentTutorialStepsSelector = createSelector(
  getAssignmentsSummary,
  getAssignmentsByTestSelector,
  getUserRole,
  (daAssignments, teacherAssignments, userRole) => {
    if (
      roleuser.DA_SA_ROLE_ARRAY.includes(userRole) &&
      isEmpty(daAssignments)
    ) {
      return assignmentNewSearchAndFolderTutorialSteps.slice(0, 2)
    }
    if (userRole === roleuser.TEACHER && isEmpty(teacherAssignments)) {
      return assignmentNewSearchAndFolderTutorialSteps.slice(0, 2)
    }
    return assignmentNewSearchAndFolderTutorialSteps
  }
)

const ACTION_HUB_JOYRIDE_ACCOUNT_AGE_MS = 3 * 24 * 60 * 60 * 1000

export const isActionHubJoyrideAccountEligible = (
  createdAt,
  now = Date.now()
) => Boolean(createdAt) && now >= createdAt + ACTION_HUB_JOYRIDE_ACCOUNT_AGE_MS

export const getActionHubJoyrideEligible = createSelector(
  getUserRole,
  getUserCreatedAt,
  (role, createdAt) =>
    role === roleuser.TEACHER && isActionHubJoyrideAccountEligible(createdAt)
)

export const getShouldDeferActionHubJoyrideAssignmentPage = createSelector(
  areViewedTutorialsLoadedSelector,
  viewedTutorialsSelector,
  (state) => state?.author_assignments?.loading,
  (state) => state.tutorial?.curriculumJoyrideRun,
  isEligibleForTeacherCommonDistrictSetting,

  (
    areViewedTutorialsLoaded,
    viewedTutorials,
    loadingAssignments,
    curriculumJoyrideRun,
    isTeacherCommonDistrictSettingPending
  ) => {
    return (
      !areViewedTutorialsLoaded ||
      loadingAssignments ||
      !viewedTutorials.includes(tutorials.newSearchAndFolderView) ||
      curriculumJoyrideRun ||
      isTeacherCommonDistrictSettingPending
    )
  }
)

export const getIsSwitchAccountJoyridePending = createSelector(
  (state) => state?.user?.user?.otherAccountCount || 0,
  (state) => state?.user?.user?.showSwitchUserTutorial || false,
  isUserLoggedInFirstTimeOnAddAccountSelector,
  viewedTutorialsSelector,
  (
    otherAccountCount,
    showSwitchUserTutorial,
    isLoggedInFirstTimeOnAddAccount,
    viewedTutorials
  ) =>
    otherAccountCount > 1 &&
    !viewedTutorials.includes(tutorials.switchAccountView) &&
    (showSwitchUserTutorial || isLoggedInFirstTimeOnAddAccount)
)

export default reducer
